import { useState, useCallback } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronDown, Plus, Trash2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SpaceContributionDrawer } from './SpaceContributionDrawer'
import { DeleteSpaceContributionDialog } from './DeleteSpaceContributionDialog'
import { useSpaceContributions } from '@/hooks/useSpaceContributions'
import {
  CONTRIBUTION_SOURCE_LABELS,
  CONTRIBUTION_SOURCES,
  CONTRIBUTION_STATUS_LABELS,
  CONTRIBUTION_STATUS_VARIANTS,
  CONTRIBUTION_SOURCE_VARIANTS,
  formatDate,
  type SpaceContributionTableRow,
  type ContributionSource,
  type ContributionStatus,
} from '@/types/space-contributions'

export function SpaceContributionsTable() {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedContribution, setSelectedContribution] = useState<SpaceContributionTableRow | null>(
    null
  )

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SpaceContributionTableRow | null>(null)

  // Filters state
  const [sourceTypeFilter, setSourceTypeFilter] = useState<ContributionSource | null>(null)
  const [statusFilter, setStatusFilter] = useState<ContributionStatus | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch data with filters
  const { data = [], isLoading } = useSpaceContributions({
    search: searchTerm,
    sourceType: sourceTypeFilter,
    status: statusFilter,
  })

  // Table columns
  const columns: ColumnDef<SpaceContributionTableRow>[] = [
    {
      accessorKey: 'unit_name',
      header: 'Unit',
      cell: ({ row }) => <div className="font-medium">{row.original.unit_name || '—'}</div>,
    },
    {
      accessorKey: 'source_type',
      header: 'Source Type',
      cell: ({ row }) => (
        <Badge variant={CONTRIBUTION_SOURCE_VARIANTS[row.original.source_type]}>
          {CONTRIBUTION_SOURCE_LABELS[row.original.source_type]}
        </Badge>
      ),
    },
    {
      accessorKey: 'tail_number',
      header: 'Aircraft',
      cell: ({ row }) => <div>{row.original.tail_number || '—'}</div>,
    },
    {
      accessorKey: 'available_from',
      header: 'Available From',
      cell: ({ row }) => <div className="text-sm">{formatDate(row.original.available_from)}</div>,
    },
    {
      accessorKey: 'available_until',
      header: 'Until',
      cell: ({ row }) => <div className="text-sm">{formatDate(row.original.available_until)}</div>,
    },
    {
      accessorKey: 'sqft',
      header: 'Sq Ft',
      cell: ({ row }) => <div className="text-right">{row.original.sqft.toLocaleString()}</div>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={CONTRIBUTION_STATUS_VARIANTS[row.original.status]}>
          {CONTRIBUTION_STATUS_LABELS[row.original.status]}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedContribution(row.original)
              setDrawerOpen(true)
            }}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDeleteTarget(row.original)
              setDeleteDialogOpen(true)
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const handleNewClick = useCallback(() => {
    setSelectedContribution(null)
    setDrawerOpen(true)
  }, [])

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelectedContribution(null)
    }
    setDrawerOpen(open)
  }, [])

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Space Contributions</h2>
          <Button onClick={handleNewClick} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Contribution
          </Button>
        </div>

        {/* Filters row */}
        <div className="flex gap-3 flex-wrap items-center">
          {/* Search */}
          <Input
            placeholder="Search units, aircraft..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />

          {/* Source Type Filter */}
          <Select
            value={sourceTypeFilter || '__all__'}
            onValueChange={(val) => setSourceTypeFilter(val === '__all__' ? null : (val as ContributionSource))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Sources</SelectItem>
              {CONTRIBUTION_SOURCES.map((source) => (
                <SelectItem key={source} value={source}>
                  {CONTRIBUTION_SOURCE_LABELS[source]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select
            value={statusFilter || '__all__'}
            onValueChange={(val) => setStatusFilter(val === '__all__' ? null : (val as ContributionStatus))}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {['projected', 'confirmed', 'consumed', 'cancelled'].map((status) => (
                <SelectItem key={status} value={status}>
                  {CONTRIBUTION_STATUS_LABELS[status as ContributionStatus]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Column visibility toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No space contributions found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Drawer for create/edit */}
      <SpaceContributionDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
        spaceContribution={selectedContribution}
      />

      {/* Delete dialog */}
      <DeleteSpaceContributionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        spaceContribution={deleteTarget}
      />
    </div>
  )
}
